import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react/jsx-runtime';

import { BedrijfSchema } from '@/api';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useLastVigerendBesluit } from '@/api/queries/besluit';
import { useAllGrondslagenInBesluiten } from '@/api/queries/besluitGrondslag/selectors/useAllGrondsalgenInBesluiten';
import { BesluitCard } from '@/components/BesluitCard/BesluitCard';
import { Button } from '@/components/Button/Button';
import { ModalTrigger } from '@/components/Modal';
import { RoleBasedAccess } from '@/components/RoleBasedAccess';
import { TextButton } from '@/components/TextButton/TextButton';
import { eStrings } from '@/constants/strings';
import {
  RegularChevronRight,
  RegularEye,
  RegularFile,
  RegularFileExport,
} from '@/icons/components';
import { BedrijfsInformatie } from '@/modals/BedrijfInformatie';
import { DateUtils } from '@/utils/dateUtils';
import { vigerendExportDocx } from '@/utils/export-docx/templates/vigerend/export-vigerend-docx';

import { BesluitCardVigerend } from './-components/BesluitCardVigerend';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/bedrijf/_layout/overzicht'
)({
  beforeLoad: () => ({
    getTitle: () => 'Overzicht',
  }),
  loader: async ({
    params: { bedrijfLineageId },
    context: { queryClient },
  }) => {
    const [, lastVigerendBesluit] = await Promise.all([
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(bedrijfLineageId)
      ),
      queryClient.ensureQueryData(
        useLastVigerendBesluit.getOptions({ bedrijfLineageId })
      ),
    ]);

    if (lastVigerendBesluit.objects?.length) {
      await queryClient.ensureQueryData(
        useAllGrondslagenInBesluiten.queryOptions({
          besluiten: lastVigerendBesluit.objects,
        })
      );
    }
  },
  component: OverzichtComponent,
});

/**
 * Route component
 */
function OverzichtComponent() {
  const { bedrijfLineageId } = Route.useParams();

  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const lastVigerendBesluit = useLastVigerendBesluit({ bedrijfLineageId });

  const besluitGrondslagen = useAllGrondslagenInBesluiten({
    besluiten: lastVigerendBesluit.data ? [lastVigerendBesluit.data] : [],
  });

  const info: { label: string; value: string }[] = [
    {
      label: 'Adresgegevens',
      value: `${bedrijf.data?.Straatnaam} ${bedrijf.data?.Huisnummer}, ${bedrijf.data?.Postcode} ${bedrijf.data?.Plaatsnaam}`,
    },
  ];

  if (bedrijf.data?.Locatienummer) {
    info.push({
      label: 'Locatienummer',
      value: bedrijf.data?.Locatienummer,
    });
  }

  if (bedrijf.data?.Bevoegd_gezag) {
    info.push({
      label: 'Bevoegd gezag',
      value: bedrijf.data?.Bevoegd_gezag,
    });
  }

  if (bedrijf.data?.Tenant) {
    info.push({
      label: 'Uitvoerende omgevingsdienst',
      value: bedrijf.data?.Tenant,
    });
  }

  if (!bedrijf.data) return null;

  return (
    <>
      <section className="border-b border-borderline-light py-5">
        <h2 className="style-h5-bold mb-3 text-gray-800">Bedrijfsinformatie</h2>

        <dl
          data-testid="description-list-testid"
          className="style-body grid grid-cols-[min-content_1fr] gap-x-5 gap-y-2"
        >
          {info.map((item) => (
            <Fragment key={item.label}>
              <dt className="col-span-1 whitespace-nowrap text-gray-800">
                {item.label}
              </dt>
              <dd className="col-span-1 text-black">{item.value}</dd>
            </Fragment>
          ))}
        </dl>
      </section>

      {lastVigerendBesluit.data && <VigerendSection bedrijf={bedrijf.data} />}

      <section className="mt-5">
        <h2 className="style-h5-bold mb-3 text-gray-800">
          {eStrings.LBL_BESLUITEN_EN_WIJZIGINGEN}
        </h2>

        {lastVigerendBesluit.data ? (
          <>
            <BesluitCardVigerend
              headingAs="h3"
              bedrijfLineageId={bedrijfLineageId}
              besluit={lastVigerendBesluit.data}
              besluitGrondslagen={besluitGrondslagen.data?.objects}
            />

            <TextButton className="mt-2 block" asChild>
              <Link
                to="/$bedrijfLineageId/bedrijf/besluiten"
                activeOptions={{
                  includeSearch: false,
                }}
                search={{ p: 1 }}
                params={{ bedrijfLineageId: bedrijf.data?.Lineage ?? '' }}
              >
                <RegularChevronRight size={14} className="mr-1 inline-block" />
                {eStrings.LBL_BEKIJK_ALLE_BESLUITEN}
              </Link>
            </TextButton>
          </>
        ) : (
          <p className="style-body italic text-placeholder">
            Er zijn nog geen besluiten die getoond kunnen worden.
          </p>
        )}
      </section>

      <RoleBasedAccess allowed={['Beheerder']} area={['omgevingsdienst']}>
        <section className="mt-5 border-t border-borderline-light py-5">
          <h2 className="style-h5-bold mb-3 text-gray-800">Bedrijfsacties</h2>

          <ModalTrigger
            modalComponent={BedrijfsInformatie}
            onOpen={() => ({
              title: 'Bedrijfsinformatie bewerken',
              bedrijfToUpdate: bedrijf.data,
            })}
            asChild
          >
            <TextButton className="inline-block">
              <RegularChevronRight size={14} className="mr-1 inline-block" />
              Bedrijfsinformatie bewerken
            </TextButton>
          </ModalTrigger>
        </section>
      </RoleBasedAccess>
    </>
  );
}

const VigerendSection = ({ bedrijf }: { bedrijf: BedrijfSchema }) => {
  const exportMutation = useMutation({ mutationFn: vigerendExportDocx });

  return (
    <section className="mt-5">
      <h2 className="style-h5-bold mb-1 text-gray-800">
        {eStrings.LBL_VIGERENDE_SITUATIE}
      </h2>

      <p className="style-body mb-2 text-gray-800">
        {eStrings.TXT_VIGERENDE_SITUATIE} {bedrijf.Naam}
      </p>

      <BesluitCard icon={RegularFile}>
        <BesluitCard.Body>
          <BesluitCard.Heading title="Vigerende situatie" />

          <BesluitCard.ButtonRow>
            <Button variant="secondary" icon={RegularEye} asChild>
              <Link
                to="/$bedrijfLineageId/viewer/vigerend"
                params={{ bedrijfLineageId: bedrijf.Lineage }}
                search={{ datum: DateUtils.formatForUrl(new Date()) }}
              >
                Bekijken
              </Link>
            </Button>

            <RoleBasedAccess
              allowed={['Beheerder', 'Vergunningverlener']}
              area={['landelijk', 'omgevingsdienst']}
            >
              <Button
                variant="secondary"
                icon={RegularFileExport}
                state={exportMutation.isPending ? 'loading' : undefined}
                onClick={() =>
                  exportMutation.mutate({
                    bedrijf,
                    date: new Date(),
                  })
                }
              >
                Exporteren
              </Button>
            </RoleBasedAccess>
          </BesluitCard.ButtonRow>
        </BesluitCard.Body>
      </BesluitCard>
    </section>
  );
};
